import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';

const SelectContainer = styled.div`
  position: relative;
  width: 100%;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  background-color: white;
  cursor: text;
  
  &:focus {
    outline: none;
    border-color: #007bff;
    box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
  }
  
  &:disabled {
    background-color: #f5f5f5;
    cursor: not-allowed;
  }
`;

const DropdownContainer = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  z-index: 1000;
  background: white;
  border: 1px solid #ddd;
  border-top: none;
  border-radius: 0 0 4px 4px;
  max-height: 200px;
  overflow-y: auto;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
`;

const OptionItem = styled.div`
  padding: 8px 12px;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 14px;
  
  &:hover {
    background-color: #f8f9fa;
  }
  
  &.selected {
    background-color: #e3f2fd;
    color: #1976d2;
  }
  
  &.disabled {
    background-color: #ffebee;
    color: #d32f2f;
    cursor: not-allowed;
    font-style: italic;
  }
  
  &.grayed {
    background-color: #f5f5f5;
    color: #666;
  }
`;

const OptionText = styled.span`
  flex: 1;
`;

const OptionBadge = styled.span`
  font-size: 12px;
  padding: 2px 6px;
  border-radius: 3px;
  margin-left: 8px;
  
  &.conflict {
    background-color: #ffebee;
    color: #d32f2f;
  }
  
  &.warning {
    background-color: #fff3e0;
    color: #f57c00;
  }
  
  &.info {
    background-color: #e3f2fd;
    color: #1976d2;
  }
`;

const NoResults = styled.div`
  padding: 12px;
  text-align: center;
  color: #666;
  font-style: italic;
`;

const SearchableEmployeeSelect = ({
  employees = [],
  value,
  onChange,
  placeholder = "Search and select employee...",
  disabled = false,
  required = false,
  filterFn = null, // Custom filter function
  displayFn = null, // Custom display function
  ...props
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  // Find selected employee based on value
  useEffect(() => {
    if (value) {
      const employee = employees.find(emp => emp.id === parseInt(value));
      setSelectedEmployee(employee);
      setSearchTerm(employee ? (displayFn ? displayFn(employee) : employee.name) : '');
    } else {
      setSelectedEmployee(null);
      setSearchTerm('');
    }
  }, [value, employees, displayFn]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
        // Reset search term to selected employee name
        if (selectedEmployee) {
          setSearchTerm(displayFn ? displayFn(selectedEmployee) : selectedEmployee.name);
        } else {
          setSearchTerm('');
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [selectedEmployee, displayFn]);

  // Filter employees based on search term
  const filteredEmployees = employees.filter(employee => {
    if (filterFn && !filterFn(employee)) {
      return false;
    }
    
    const searchLower = searchTerm.toLowerCase();
    const name = employee.name?.toLowerCase() || '';
    const username = employee.username?.toLowerCase() || '';
    const role = employee.role?.toLowerCase() || '';
    
    return name.includes(searchLower) || 
           username.includes(searchLower) || 
           role.includes(searchLower);
  });

  const handleInputFocus = () => {
    setIsOpen(true);
  };

  const handleInputChange = (e) => {
    setSearchTerm(e.target.value);
    if (!isOpen) {
      setIsOpen(true);
    }
  };

  const handleOptionClick = (employee) => {
    setSelectedEmployee(employee);
    setSearchTerm(displayFn ? displayFn(employee) : employee.name);
    setIsOpen(false);
    onChange && onChange({ target: { value: employee.id } });
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (filteredEmployees.length === 1 && !filteredEmployees[0].isDisabled) {
        handleOptionClick(filteredEmployees[0]);
      }
    } else if (e.key === 'Escape') {
      setIsOpen(false);
      if (selectedEmployee) {
        setSearchTerm(displayFn ? displayFn(selectedEmployee) : selectedEmployee.name);
      } else {
        setSearchTerm('');
      }
    }
  };

  const getDefaultDisplayName = (employee) => {
    let name = employee.name;
    
    if (employee.role) {
      name += ` (${employee.role})`;
    }
    
    return name;
  };

  const getOptionBadge = (employee) => {
    if (employee.isDisabled && employee.conflictReason) {
      return <OptionBadge className="conflict">Conflict</OptionBadge>;
    }
    if (employee.isGrayed && employee.conflictReason) {
      return <OptionBadge className="warning">Warning</OptionBadge>;
    }
    if (employee.is_moveable) {
      return <OptionBadge className="info">Moveable</OptionBadge>;
    }
    return null;
  };

  return (
    <SelectContainer ref={containerRef} {...props}>
      <SearchInput
        ref={inputRef}
        type="text"
        value={searchTerm}
        onChange={handleInputChange}
        onFocus={handleInputFocus}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        disabled={disabled}
        required={required}
      />
      
      {isOpen && (
        <DropdownContainer>
          {filteredEmployees.length === 0 ? (
            <NoResults>
              {searchTerm ? `No employees found matching "${searchTerm}"` : 'No employees available'}
            </NoResults>
          ) : (
            filteredEmployees.map((employee) => (
              <OptionItem
                key={employee.id}
                className={`
                  ${selectedEmployee?.id === employee.id ? 'selected' : ''}
                  ${employee.isDisabled ? 'disabled' : ''}
                  ${employee.isGrayed ? 'grayed' : ''}
                `}
                onClick={() => !employee.isDisabled && handleOptionClick(employee)}
              >
                <OptionText>
                  {displayFn ? displayFn(employee) : getDefaultDisplayName(employee)}
                  {employee.conflictReason && (
                    <div style={{ fontSize: '12px', color: employee.isDisabled ? '#d32f2f' : '#666', marginTop: '2px' }}>
                      {employee.conflictReason}
                    </div>
                  )}
                </OptionText>
                {getOptionBadge(employee)}
              </OptionItem>
            ))
          )}
        </DropdownContainer>
      )}
    </SelectContainer>
  );
};

export default SearchableEmployeeSelect;